// Firebase.
import { FirebaseCtx, createInitialisedFirebase } from "igfd-commons";

import React, { Suspense, lazy } from "react";
import { QueryClientProvider } from "react-query";
// Redux.
import { Provider, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

// MUI.
import { CssBaseline, ThemeProvider } from "@mui/material";

import {
  CircularProgressCenter,
  ErrorBoundary,
  createDefaultTheme,
  useDeviceDimensions,
} from "igfd-ui/src/app-index";

import { axiosInstances } from "igfd-commons/src/http";
import { createQueryClient } from "igfd-commons/src/query-client-config";
import { WithFallback } from "igfd-commons/src/utils/lazy";

// Contexts, layout, and firebase.
import {
  AuthStateProvider,
  DashboardSnackbarProvider,
} from "@dashboard/context";

import { WorkspaceSwitcherProvider } from "./components/admin/shell/WorkspaceSwitcher/WorkspaceSwitcherProvider";
import { RouteErrorPage } from "./components/errors/RouteErrorPage";
import { MobileOverlay } from "./components/mobile-overlay/MobileOverlay";
import { config } from "./environment/config";
import {
  AuthenticatedLayout,
  MenusAndDishesLayout,
  PageLayout,
  RestaurantManagementLayout,
  UnauthenticatedLayout,
} from "./layouts";
import { storeWithThunkExtraArgs } from "./store/createReduxStore";
import { ReduxState } from "./types";

// pages.
const LoginPage = lazy(() => import("./components/auth/login/LoginPage"));
const SignUpPage = lazy(() => import("./components/auth/signup/SignUpPage"));
const CreateRestaurantPage = lazy(
  () => import("./components/restaurant/CreateRestaurantPage")
);
const AccountPage = lazy(async () => {
  return import("./components/account/AccountPage");
});
const ResetPasswordPage = lazy(
  () => import("./components/auth/resetPassword/ResetPasswordPage")
);
const MenuContentPage = lazy(
  () => import("./components/menus/MenuContentPage")
);
const DishesPage = lazy(() => import("./components/dishes/DishesPage"));
const ProductSectionPage = lazy(
  () => import("./components/products/ProductSectionPage")
);
const NotFoundPage = lazy(() => import("./components/errors/NotFoundPage"));
const RestaurantPage = lazy(
  () => import("./components/restaurant/RestaurantPage")
);
const AnalyticsPage = lazy(() => import("./analytics/AnalyticsPage"));

const AnalyticsRoute = () => {
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  return userIsAdmin ? (
    WithFallback(AnalyticsPage)
  ) : (
    <Navigate to="/menus" replace />
  );
};
const IntegrationsPage = lazy(
  () => import("./components/integrations/IntegrationsPage")
);

const IntegrationsRoute = () => {
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );
  return userIsAdmin ? (
    WithFallback(IntegrationsPage)
  ) : (
    <Navigate to="/menus" replace />
  );
};
const MenuUrlsPage = lazy(() => import("./components/menu-urls/MenuUrlsPage"));
const ThemesPage = lazy(() => import("./components/themes/ThemesPage"));

const AdminLayout = lazy(() => import("./components/admin/shell/AdminLayout"));
const ToolsPage = lazy(
  () => import("./components/admin/shell/tools/ToolsPage")
);
const WorkspacesPage = lazy(
  () => import("./components/admin/shell/workspaces/WorkspacesPage")
);

const AclPage = lazy(() => import("./components/admin/acl/AclPage"));
const AllergenInsightsPage = lazy(
  () => import("./components/admin/allergen-insights/AllergenInsightsPage")
);
const PlanLimitsPage = lazy(
  () => import("./components/admin/shell/plan-limits/PlanLimitsPage")
);

const withFallbackNode = (node: React.ReactNode) => (
  <Suspense fallback={<CircularProgressCenter />}>{node}</Suspense>
);

export const App = () => {
  const theme = createDefaultTheme({});
  const initialisedFirebase = createInitialisedFirebase(config.firebase);

  const { store, persistor } = storeWithThunkExtraArgs(
    initialisedFirebase,
    axiosInstances
  );

  const queryClient = createQueryClient();
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route
          element={<UnauthenticatedLayout />}
          ErrorBoundary={RouteErrorPage}
        >
          <Route index path="/login" element={WithFallback(LoginPage)} />
          <Route path="/signup" element={WithFallback(SignUpPage)} />
          <Route
            path="/reset-password"
            element={WithFallback(ResetPasswordPage)}
          />
        </Route>

        <Route element={<AuthenticatedLayout />} ErrorBoundary={RouteErrorPage}>
          <Route index path="/" element={<Navigate to="/menus" />} />
          <Route
            // This page is shown along side the header, which is depends on the number of restaurants being known.
            // Hence it sits inside the restaurant management layout.
            path="/restaurants/create"
            element={WithFallback(CreateRestaurantPage)}
          />
          <Route path="/account" element={WithFallback(AccountPage)} />
          <Route
            // Outside the restaurant layout: renders with nothing selected.
            path="/admin/workspaces"
            element={
              <WorkspaceSwitcherProvider>
                {WithFallback(AdminLayout)}
              </WorkspaceSwitcherProvider>
            }
          >
            <Route index element={WithFallback(WorkspacesPage)} />
          </Route>
          <Route
            element={<RestaurantManagementLayout />}
            ErrorBoundary={RouteErrorPage}
          >
            <Route
              path="/acl"
              element={withFallbackNode(
                <PageLayout>
                  <AclPage />
                </PageLayout>
              )}
            />
            <Route path="/admin" element={WithFallback(AdminLayout)}>
              <Route index element={<Navigate to="/admin/tools" replace />} />
              <Route path="tools" element={WithFallback(ToolsPage)} />
              <Route path="members" element={withFallbackNode(<AclPage />)} />
              <Route
                path="plan"
                element={withFallbackNode(<PlanLimitsPage />)}
              />
              <Route element={<MenusAndDishesLayout />}>
                <Route
                  path="integrations/:partner?"
                  element={withFallbackNode(
                    <IntegrationsPage basePath="/admin/integrations" />
                  )}
                />
                <Route path="analytics" element={<AnalyticsRoute />} />
              </Route>
            </Route>
            <Route element={<MenusAndDishesLayout />}>
              <Route path="/settings" element={WithFallback(RestaurantPage)} />
              <Route
                path="/insights"
                element={withFallbackNode(<AllergenInsightsPage />)}
              />
              <Route
                path="/integrations/:partner?"
                element={<IntegrationsRoute />}
              />
              <Route path="/analytics" element={<AnalyticsRoute />} />
              <Route
                path="/products/:section?"
                element={WithFallback(ProductSectionPage)}
              />
              <Route
                path="/menus/:menuId?"
                element={WithFallback(MenuContentPage)}
              />
              <Route path="/dishes" element={WithFallback(DishesPage)} />
              <Route path="/menu-urls" element={WithFallback(MenuUrlsPage)} />
              <Route
                path="/themes/:themeId?"
                element={WithFallback(ThemesPage)}
              />
            </Route>
          </Route>
        </Route>

        <Route
          path="*"
          element={WithFallback(NotFoundPage)}
          ErrorBoundary={RouteErrorPage}
        />
      </>
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <FirebaseCtx.Provider value={initialisedFirebase}>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <DashboardSnackbarProvider>
                  <AuthStateProvider>
                    <BlockingMobileView>
                      <RouterProvider router={router} />
                    </BlockingMobileView>
                  </AuthStateProvider>
                </DashboardSnackbarProvider>
              </PersistGate>
            </Provider>
          </QueryClientProvider>
        </FirebaseCtx.Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const BlockingMobileView = ({ children }: React.PropsWithChildren) => {
  const { isLargeScreen } = useDeviceDimensions();
  // allow admins to use the app on mobile.
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  // when an admin is accessing the site on mobile, we show the full experience.
  // However until login happens, the user is not known. and hence we don't know
  // if they are an admin. Hence this special routing exists.
  const mobileRouter = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route
          element={<UnauthenticatedLayout />}
          ErrorBoundary={RouteErrorPage}
        >
          <Route index path="/login-admin" element={WithFallback(LoginPage)} />
          <Route path="*" element={<MobileOverlay />} />
        </Route>
      </>
    )
  );

  if (isLargeScreen || userIsAdmin) {
    return children;
  }

  return <RouterProvider router={mobileRouter} />;
};
